// Import react elements
import { useContext, useEffect, useState } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import strich api calls
import {
    deletePodDesign,
} from "api/APIStrichCalls";

// Import hooks
import { useToast } from "global/hooks/useToast";


// Import components
import CreateButton from "global/components/CreateButton";
import CustomisableTable from 'global/components/CustomisableTable';
import CustomisableTableSelectedRowsActionsDropwdown from 'global/components/CustomisableTableActionsDropdown';

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Format the columns for the pod designs table - Need to set these values in fetchPodDesigns mapped array
const initialTableColumns = [

    // Select tick box at left of table rows - if we want to multi select designs and action them
    { key: 'select', label: '', resizable: false, minWidth: 40, maxWidth: 40, draggable: false, textFilter: false },

    { key: 'view', label: '', resizable: false, minWidth: 55, maxWidth: 55, draggable: false, textFilter: false },

    { key: 'preview_image_url', label: '', resizable: true, minWidth: 300, maxWidth: 800, draggable: false, textFilter: false },
     
    // Display the title with the strich pill (if it's a strich template)
    { key: 'titleWithPill', label: 'Title', resizable: true, minWidth: 150, maxWidth: 350, draggable: true, textFilter: true },
   
    { key: 'created', label: 'Date Created', resizable: true, minWidth: 100, maxWidth: 150, draggable: true, textFilter: true },

    { key: 'active', label: 'Active', resizable: true, minWidth: 90, maxWidth: 200, draggable: true, textFilter: false },

];



const PodDesignsTable = ({ 
    designOrTemplate,
    podDesignsData,
    handleSelectPodDesign,
    loadingPodDesigns,
    fetchPodDesigns,
    handleToggleDesignActive,
    rememberPodDesignsScrollPosition,
    onAdd,
}) => {

    //console.log("podDesignsData: ", podDesignsData);

    // Get the token from AuthContext
    const { token} = useContext(AuthContext);

    // Define toast options
    const { toastError } = useToast();

    // Templates do not have a user-facing design active state
    const tableColumns = designOrTemplate === "template"
        ? initialTableColumns.filter((column) => column.key !== "active")
        : initialTableColumns;

    // Set filters state - needs passing to filters component and table component
    const [filters] = useState({});

    // Set state for selected table rows
    const [selectedPodDesignRows, setSelectedPodDesignRows] = useState([]);
    
    // Set state for selected table rows
    const [numberOfDisplayedPodProducts, setNumberOfDisplayedPodProducts] = useState(0);



    // Watch when the designOrTemplate value changes and if so clear the selected rows
    useEffect(() => {

        // Clear the selected rows
        setSelectedPodDesignRows([]);

    }, [designOrTemplate]);



    // Delete POD Product
    const handleDeletePodDesign = async (podProductID) => {

        // Ensure we always have an array
        const podProductIds = Array.isArray(podProductID) ? podProductID : [podProductID];

        // Check if the selected design is a Strich template
        const isStrichTemplate =
            designOrTemplate === "template" &&
            podDesignsData?.some(
                (podDesign) =>
                    podProductIds.includes(podDesign.id) &&
                    podDesign.source === "strich"
            );

        // If the selected design is a Strich template
        if (isStrichTemplate) {

            // Toast an error 
            toastError({ title: "Error", message: "Cannot delete Strich templates.", autoCloseMs: 6000 });

            // Return early
            return;

        }

        // Get the design
        const title =
            podDesignsData?.find(
                (podDesign) =>
                    podProductIds.includes(podDesign.id) 
            )?.title;


        // Display confirmation
        DisplayConfirmationDialog(

            `<h3>Are you sure?</h3> <br/> Delete POD ${designOrTemplate === "template" ? "Template" : "Design"}:<br/><br/> ${title} <br/><br/><span class='warning-message'>WARNING: This cannot be undone.</span>`,
            "Delete",
            "Cancel",
            async function (is_confirmed) {

                // If user confirms, proceed with deletion
                if (is_confirmed) {

                    // Remember the current list position before refreshing it after deletion
                    if (typeof rememberPodDesignsScrollPosition === "function") {
                        rememberPodDesignsScrollPosition();
                    }

                    // Delete pod design by id
                    const response = await deletePodDesign(token, designOrTemplate, podProductID);

                    // Check if it deleted
                    if (response?.success) {

                        // It did so re fetch the designs to update the table
                        fetchPodDesigns(designOrTemplate);
                        return;

                    }

                    // Surface dependency errors and any other API failure
                    toastError({
                        title: "Error",
                        message: response?.error_message || `Could not delete ${designOrTemplate}.`,
                        autoCloseMs: 6000,
                    });

                }

            }

        );

    };



    
    // Create menu options array - used for dropdown per table row 
    const podDesignActionsConfig = [
        {
            id: "view", // Unique id
            label: 'View Details', // Title label
            clickFunction: handleSelectPodDesign, // Click function used for table row button
            icon: '✏️', // Optional icon
            disabled: false, // Disabled state
            singleItemAction: true, // Is this action just for single item
        },
        {
            id: "delete",
            label: 'Delete',
            clickFunction: handleDeletePodDesign,
            icon: '🗑️',
            disabled: false,
            color: 'red',
        },

    ];
    


    return (

        <div>

            <div className="table_tally_options_bar horizontal-align-content-center flex-justify-left-right">

                { /* Total number of orders / total selected orders out of total orders PLUS options drop down */}
                <div className="orders_tally" style={{ opacity: selectedPodDesignRows.length > 0 ? "1" : "0.5" }}>

                    { /* Clear selected rows */}
                    {selectedPodDesignRows.length > 0 &&
                        <div className="clear_selected_tally" onClick={()=>setSelectedPodDesignRows([])}>
                            ⨯
                        </div>
                    }
                    <div>

                        <span>

                            {loadingPodDesigns ? 

                                "Loading..."
                            
                                :
                            
                                <>
                                    
                                    {/* Display number of selected designs */}
                                    {selectedPodDesignRows.length > 0 ? selectedPodDesignRows.length + " of " :  ""}

                                    {/* Display total number of designs/s  */}
                                    {numberOfDisplayedPodProducts} {designOrTemplate.charAt(0).toUpperCase() + designOrTemplate.slice(1)}{numberOfDisplayedPodProducts === 1 ? "" : "s" }
                                      
                                </>

                            }

                            </span>
                    </div>

                </div>

                
                <div className="horizontal-align-content-center">

                    {selectedPodDesignRows.length > 0 &&

                        // Show design/template actions dropdown
                        <CustomisableTableSelectedRowsActionsDropwdown
                            tableType={"PodDesigns"}
                            tableData={podDesignsData}
                            fetchTableData={fetchPodDesigns}
                            handleSelectRow={handleSelectPodDesign}
                            selectedTableRows={selectedPodDesignRows}
                            setSelectedRows={setSelectedPodDesignRows}
                            designOrTemplate={designOrTemplate}
                        />

                    }

                    <CreateButton
                        title={`+ Add POD ${designOrTemplate === "design" ? "Design" : "Template"}`}
                        size="m"
                        onClick={onAdd}
                    />

                </div>
                

            </div>

            <div className="table__full-width-scroll">

                <CustomisableTable
                    tableData={podDesignsData}
                    initialTableColumns={tableColumns}
                    filterKeys={[]}
                    filters={filters}
                    selectedRows={selectedPodDesignRows}
                    setSelectedRows={setSelectedPodDesignRows}
                    loadingData={loadingPodDesigns}
                    noDataAvaiableMessage={`No POD ${designOrTemplate}s to display.`}
                    setNumberOfDisplayedDataRows={setNumberOfDisplayedPodProducts}
                    menuOptions={podDesignActionsConfig} 

                    handleRowSingleClick={handleSelectPodDesign}
    
                    // Provide a link builder function instead
                    //getRowLink={(row) => `/designs/${designOrTemplate === "template" ? "templates/" : ""}${designOrTemplate}-${row.id}`} 

                    // Active state only applies to user-facing designs
                    activeTogglefunction={designOrTemplate === "design" ? handleToggleDesignActive : undefined}
                />

            </div>

        </div>

    )

};

export default PodDesignsTable;